import React, { useEffect } from "react"
import { Card, Row, Col, Alert, Input, Divider, Form, notification, DatePicker } from "antd"
import { Typography } from "antd"
import FormInput from "components/FormInput"
import ParListSelect from "components/ParListSelect"
import InputCurrency from "components/InputCurrency"
import { getThirdPartyAuthorizationByTpreference, getThirdPartyById } from "services/thirdParty"
import {
  tpAuthorizationUseSFS,
  type TpAuthorizationUseSFSRequest,
} from "services/proposal"
import moment from "moment"

const { Title, Text } = Typography

type InvoiceUnreservedStepProps = {
  form: any
  isInspect: boolean
  suggestedBrand: string
  setSuggestedBrand: (brand: string) => void
  isManuallySelected: boolean
  setIsManuallySelected: (selected: boolean) => void
  isLoadingAuth: boolean
  setIsLoadingAuth: (loading: boolean) => void
  lastFetchKey: string
  setLastFetchKey: (key: string) => void
  onCreateReservation: {
    isCreatingReservation: boolean
    setIsCreatingReservation: (creating: boolean) => void
    setProposalResponse: (response: any) => void
    setCurrent: (step: number) => void
    setCompletedSteps: (steps: number[]) => void
    userInfo: any
  }
}

const InvoiceUnreservedStep: React.FC<InvoiceUnreservedStepProps> = ({
  form,
  isInspect,
  suggestedBrand,
  setSuggestedBrand,
  isManuallySelected,
  setIsManuallySelected,
  isLoadingAuth,
  setIsLoadingAuth,
  lastFetchKey,
  setLastFetchKey,
  onCreateReservation
}) => {
  const codeClientFactureValue = Form.useWatch("codeClientFacture", form)
  const familleFinancementValue = Form.useWatch("familleFinancement", form)
  const codeMarqueValue = Form.useWatch("codeMarque", form)
   const vinValue = Form.useWatch("VIN", form)
  const handleBrandSelection = async (selectedBrand: string, option: any) => {
    form.setFieldValue("brandLabel", option?.children || selectedBrand)
    form.setFieldValue("codeMarque", selectedBrand)
    setIsManuallySelected(true)
    
    notification.info({
      message: "Marque sélectionnée",
      description: `Marque "${selectedBrand}" sélectionnée`,
      duration: 3,
    })
  }

const createReservation = async () => {
  const {
    setIsCreatingReservation,
    setProposalResponse,
    setCurrent,
    setCompletedSteps,
    userInfo
  } = onCreateReservation

  try {
 

    const values = await form.validateFields([
      'codeClientFacture', 'familleFinancement', 'VIN', 'reservationAmount', 
      'codeMarque', 'cafNumber', 'propNumber', 'dateEmissionFacture'
    ])

 
        setIsCreatingReservation(true)
    
    let thirdPartyData = null
    let detectedBrand = values.codeMarque 
    
    try {
      const authResponse = await getThirdPartyAuthorizationByTpreference({
        tpreference: values.codeClientFacture,
        prcode: values.familleFinancement,
      })
      
      if (authResponse.code === "200" && authResponse.data?.tpid) {
        const tpid = authResponse.data.tpid
        
     
        if (authResponse.data?.authbrand && !isManuallySelected) {
          detectedBrand = authResponse.data.authbrand
          form.setFieldValue("codeMarque", detectedBrand)
          form.setFieldValue("brandLabel", detectedBrand)
        }
        
        const thirdPartyResponse = await getThirdPartyById({
          tpid: tpid
        })
        
        if (thirdPartyResponse.code === "200" && thirdPartyResponse.data) {
          thirdPartyData = thirdPartyResponse.data
        }
      }
    } catch (error) {
      console.warn("Could not fetch thirdparty data:", error)
    }


    console.log("=== DEBUG: Date formatting ===")
    
    let formattedDate
    if (values.dateEmissionFacture) {
      console.log("dateEmissionFacture exists:", values.dateEmissionFacture)
      console.log("moment(dateEmissionFacture):", moment(values.dateEmissionFacture))
      formattedDate = moment(values.dateEmissionFacture).format("YYYY-MM-DD")
      console.log("Formatted date:", formattedDate)
    } else {
      console.log("dateEmissionFacture is null/undefined, using current date")
      formattedDate = moment().format("YYYY-MM-DD")
      console.log("Using current date:", formattedDate)
    }

    const requestData: TpAuthorizationUseSFSRequest = {
      prcode: values.familleFinancement,
      tpreference: values.codeClientFacture,
      taxcode: thirdPartyData?.generalData?.tptaxid || "",
      fiscalid: thirdPartyData?.generalData?.tpfiscalid || "",
      financedamount: values.reservationAmount,
      mgid: userInfo?.mgid?.toString() || "1",
      movementcode: "SASK",
      requesteddate: formattedDate,
      vehserialnumber: values.VIN,
      priceht: values.reservationAmount,
      pricettc: values.reservationAmount,
      proporeference: values.propNumber,
      constructorcode: values.codeMarque,
      treatmentOrder: 1,
      brand: values.codeMarque,
      cafNumber: values.cafNumber,
      startdate: formattedDate,
      rejectionReasonCode: "",
      descriptionOfRejection: "",
      paymentDelay: "",
      paymentDelayType: "",
      isTransformedVehicle: false
    }

    
    const response = await tpAuthorizationUseSFS(requestData)
    
    if (response.code === "200" && response.data) {
      setProposalResponse(response.data)
      setCurrent(2)
      setCompletedSteps([0, 1])
     
      form.setFieldsValue({
        dateEmissionFacture: moment(values.dateEmissionFacture),
        dateDebutCalculInteret: moment(values.dateEmissionFacture),
        montantTTC: values.reservationAmount,
        montantHTVehicule: values.reservationAmount,
        montantTVA: 0,
        montantFrais: 0,
        montantAutresTaxes: 0,
        dureeFranchise: 0,
        codePays: "FR",
        codeDevise: "EUR",
        typeVehicule: "1"
      })
      
      notification.success({
        message: "Réservation créée avec succès",
        description: `Référence: ${response.data.propreference || response.data.ctreference}. Vous pouvez maintenant créer la facture.`,
        duration: 6,
      })
    } else {
      throw new Error(response.message || "Erreur lors de la création de réservation")
    }
  } catch (error: any) {
    console.error("=== DEBUG: Error occurred ===", error)
    notification.error({
      message: "Erreur de création",
      description: error.message || "Impossible de créer la réservation",
      duration: 5,
    })
  } finally {
    setIsCreatingReservation(false)
  }
}

  useEffect(() => {
    const fetchAndSuggestBrand = async () => {
      const fetchKey = `${codeClientFactureValue}_${familleFinancementValue}`

      if (!codeClientFactureValue || codeClientFactureValue.length !== 7 || !familleFinancementValue) {
        setSuggestedBrand("")
        if (isLoadingAuth) setIsLoadingAuth(false)
        return
      }

      
      if (lastFetchKey === fetchKey) {
        setIsLoadingAuth(false)
        return
      }


      if (isManuallySelected) {
        setIsLoadingAuth(false)
        return
      }

      try {
        setIsLoadingAuth(true)
        setLastFetchKey(fetchKey)

        const basicResponse = await getThirdPartyAuthorizationByTpreference({
          tpreference: codeClientFactureValue,
          prcode: familleFinancementValue,
        })

        if (basicResponse.code === "200" && basicResponse.data?.authbrand) {
          const detectedBrand = basicResponse.data.authbrand
          setSuggestedBrand(detectedBrand)
          
          const currentBrand = form.getFieldValue("codeMarque")
          if (!isManuallySelected && (!currentBrand || currentBrand === "")) {
            form.setFieldValue("codeMarque", detectedBrand)
            form.setFieldValue("brandLabel", detectedBrand)
           
          }
        } else {
          setSuggestedBrand("")
        }
      } catch (error: any) {
        setSuggestedBrand("")
      } finally {
        setIsLoadingAuth(false)
      }
    }

    const timeoutId = setTimeout(fetchAndSuggestBrand, 500)
    return () => clearTimeout(timeoutId)
  }, [
    codeClientFactureValue,
    familleFinancementValue,
    isManuallySelected,
    lastFetchKey,
    form,
    isLoadingAuth,
    setSuggestedBrand,
    setIsLoadingAuth,
    setLastFetchKey
  ])

 
  useEffect(() => {
    const currentKey = `${codeClientFactureValue}_${familleFinancementValue}`
    if (codeClientFactureValue && familleFinancementValue) {
      if (lastFetchKey !== currentKey && lastFetchKey !== "") {
        setIsManuallySelected(false)
        setSuggestedBrand("")
      }
    }
  }, [codeClientFactureValue, familleFinancementValue, lastFetchKey, setIsManuallySelected, setSuggestedBrand])

  return (
    <Card title="VIN Non Réservé - Pré-facturation" className="wizard-step-card">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Alert
            message="VIN Non Réservé"
            description="Ce VIN n'est pas réservé. Veuillez saisir les informations pour créer une réservation."
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={10}>
          <Form.Item
            name="codeClientFacture"
            label="Code Client (RRDI)"
            rules={[
              { required: true, message: "Veuillez saisir le code client" },
              { len: 7, message: "Le code client doit contenir exactement 7 caractères" }
            ]}
          >
            <Input
              placeholder="Code Client (7 caractères)"
              maxLength={7}
              disabled={isInspect}
            />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name="codeMarque"
            label="Code Marque"
            rules={[{ required: true, message: "Veuillez sélectionner la marque" }]}
            extra={
              suggestedBrand && !isManuallySelected ? (
                <Text style={{ color: "#1890ff", fontSize: "12px" }}>
                 
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Sélectionnez une marque
                </Text>
              )
            }
          >
            <ParListSelect
              parcode="TPBRAND"
              disabled={isInspect}
              loading={isLoadingAuth}
              placeholder={
                isLoadingAuth ? "Recherche automatique en cours..." : "Sélectionnez une marque"
              }
              onSelect={handleBrandSelection}
              allowClear
              showSearch
              filterOption={(input: string, option: any) =>
                (option?.children?.toString() || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            />
          </Form.Item>
        </Col>
       
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={10}>
       <Form.Item
  name="dateEmissionFacture" 
  label="Date Facture"
  rules={[{ required: true, message: "Veuillez sélectionner la date de facture" }]}
  initialValue={moment()} 
>
  <DatePicker
    placeholder="Date "
    className="w-100"
    disabled={isInspect}
    format="YYYY-MM-DD" 
    onChange={(date) => {
      console.log("DatePicker onChange:", date);
      form.setFieldValue('dateEmissionFacture', date);
    }}
  />
</Form.Item>
        </Col>
         <Col span={10}>
          <Form.Item
            name="reservationAmount"
            label="Montant "
            rules={[{ required: true, message: "Veuillez saisir le montant" }]}
          >
            <InputCurrency
              placeholder="Montant TTC"
              min={0}
              disabled={isInspect}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
       <Form.Item name="propNumber" hidden>
    <Input />
  </Form.Item>
        </Col>
      </Row>
    </Card>
  )
}

export default InvoiceUnreservedStep